import { DateTime } from "luxon";

import type { SolarState } from "./sun";
import { formatCoord } from "./geo";
import { inZone } from "./tzrules";

interface ClockRefs {
  utcTime: HTMLElement;
  utcDate: HTMLElement;
  localTime: HTMLElement;
  localDate: HTMLElement;
  localLabel: HTMLElement;
  localCode: HTMLElement;
  subsolarCoord: HTMLElement;
  subsolarDecl: HTMLElement;
  eot: HTMLElement;
  observerCoord: HTMLElement;
}

export class ClockOverlay {
  private refs: ClockRefs;
  private localZone: string;

  constructor() {
    this.refs = {
      utcTime: byId("utc-time"),
      utcDate: byId("utc-date"),
      localTime: byId("local-time"),
      localDate: byId("local-date"),
      localLabel: byId("local-label"),
      localCode: byId("local-code"),
      subsolarCoord: byId("subsolar-coord"),
      subsolarDecl: byId("subsolar-decl"),
      eot: byId("eot"),
      observerCoord: byId("observer-coord"),
    };

    this.localZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";

    // Pretty label + short IATA-esque code
    this.refs.localLabel.textContent = prettyZone(this.localZone);
    this.refs.localCode.textContent = zoneCode(this.localZone);
  }

  update(now: DateTime, solar: SolarState): void {
    const utc = now.toUTC();
    this.refs.utcTime.textContent = utc.toFormat("HH:mm:ss");
    this.refs.utcDate.textContent = utc.toFormat("ccc · dd LLL yyyy");

    const local = inZone(now, this.localZone);
    this.refs.localTime.textContent = local.toFormat("HH:mm:ss");
    this.refs.localDate.textContent = local.toFormat(
      `ccc · dd LLL yyyy · ZZ`,
    );

    this.refs.subsolarCoord.textContent = formatCoord(solar.lat, solar.lng);
    this.refs.subsolarDecl.textContent = `${solar.lat >= 0 ? "+" : ""}${solar.lat.toFixed(3)}°`;
    this.refs.eot.textContent = formatEot(solar.eotMinutes);
  }

  setObserverCoord(lat: number, lng: number): void {
    this.refs.observerCoord.textContent = formatCoord(lat, lng);
  }

  get zone(): string {
    return this.localZone;
  }
}

function byId(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el;
}

function prettyZone(tz: string): string {
  const city = tz.split("/").pop() ?? tz;
  return city.replace(/_/g, " ");
}

function zoneCode(tz: string): string {
  // Produce a short code from the city name, e.g. "Europe/Paris" → "PAR".
  const city = tz.split("/").pop() ?? tz;
  const letters = city.replace(/[^A-Za-z]/g, "");
  return letters.slice(0, 3).toUpperCase() || "LOC";
}

function formatEot(minutes: number): string {
  const sign = minutes >= 0 ? "+" : "−";
  const abs = Math.abs(minutes);
  const m = Math.floor(abs);
  const s = Math.round((abs - m) * 60);
  return `${sign}${m.toString().padStart(2, "0")}m ${s
    .toString()
    .padStart(2, "0")}s`;
}
